import React, { useEffect, useState } from 'react';
import styles from './Navbar.module.scss';
import { ReactComponent as LocationIcon } from '../../Icons/Location.svg';
import { ReactComponent as AngleDownIcon } from '../../Icons/AngleDown.svg';
import BackButton from '../BackButton';
import { useSelector } from 'react-redux';
import { getAddressFromLatLng } from '../../services';
import useSessionStorage from '../../hooks/useStorage';
import { logEvent } from '../../../../../utils/logEvent';

const Navbar = ({
  style = {},
  onBack,
  label,
  onClick,
  isTransparent,
  showLocation = false,
  withDivider = true,
  rightButtons,
  textColor = '#000',
  bgColor = '#ffffff',
  iconColor = '#fff',
  locationIconStyle = {},
  noPadding = false,
  addressColor = '#000',
  hideBackButton = false,
  backIconColor,
  moduleBasedLogin = false,
}) => {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const authToken = useSelector((state) => state?.auth?.token);
  const { setToStorage, getFromStorage } = useSessionStorage();

  useEffect(() => {
    console.log('useEffect navbar');
  }, []);

  const getBrowserLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        // User may have picked a saved address while prompt was open — don't
        // clobber their choice.
        if (getFromStorage('selected_address')) return;
        setIsFetchingLocation(true);
        const { latitude, longitude } = pos.coords;
        getAddressFromLatLng(latitude, longitude, authToken)
          .then((res) => {
            if (getFromStorage('selected_address')) return;
            setToStorage('selected_address', {
              city: res?.result?.city,
              countryCode: res?.result?.countryCode,
              formattedAddress: res?.result?.formattedAddress,
              lat: res?.result?.latitude,
              long: res?.result?.longitude,
              pincode: res?.result?.postalCode,
              province: res?.result?.province,
              state: res?.result?.state,
              street: res?.result?.street,
            });
            window.dispatchEvent(new Event('location_selected'));
          })
          .finally(() => setIsFetchingLocation(false));
      },
      () => {
        // If user already selected an address while prompt was pending, keep
        // that — don't overwrite with "permission denied".
        if (getFromStorage('selected_address')) return;
        setCurrentLocation({ formattedAddress: 'Location permission denied' });
      }
    );
  };

  useEffect(() => {
    if (!showLocation) return;

    const stored = getFromStorage('selected_address');
    if (stored) {
      setCurrentLocation(stored);
    } else {
      getBrowserLocation();
    }

    const handleLocationSelected = () => {
      const latest = getFromStorage('selected_address');
      if (latest) {
        setCurrentLocation(latest);
      }
    };

    window.addEventListener('location_selected', handleLocationSelected);
    return () => {
      window.removeEventListener('location_selected', handleLocationSelected);
    };
  }, [showLocation]);

  return (
    <nav
      className={`${styles.root} ${withDivider ? styles.borderBottom : ''}`}
      style={{
        ...style,
        background: isTransparent ? 'transparent' : bgColor,
        alignItems: showLocation ? 'flex-start' : 'center',
        padding: noPadding ? '0' : '16px',
      }}
    >
      <div
        className={styles.navBarContent}
        style={{ marginBottom: showLocation && !noPadding ? 4 : 0 }}
      >
        <div className={styles.controls}>
          {hideBackButton ? (
            <div style={{ width: 32 }} />
          ) : (
            <BackButton
              color={backIconColor || (isTransparent ? '#ffffff' : '#000000')}
              x
              onClick={onBack}
              noPadding={noPadding}
            />
          )}
          <p className={styles.label} style={{ color: textColor }}>
            {label}
          </p>
        </div>
        {showLocation && (
          <div
            className={styles.locationContainer}
            onClick={() => {
              logEvent('Address Bar Clicked', {});
              onClick?.();
            }}
          >
            <div
              style={{ ...locationIconStyle }}
              className={styles.locationIconContainer}
            >
              <LocationIcon color={locationIconStyle.color || '#fff'} />
            </div>

            <p className={styles.location} style={{ color: addressColor }}>
              {isFetchingLocation ? (
                'Fetching location...'
              ) : currentLocation?.title ? (
                <>
                  <span style={{ fontWeight: 700 }}>
                    {currentLocation.title}
                  </span>
                  : {currentLocation.address}
                </>
              ) : (
                currentLocation?.address ||
                currentLocation?.formattedAddress ||
                'Select address'
              )}
            </p>

            <AngleDownIcon color={iconColor} style={{ marginLeft: 4 }} />
          </div>
        )}
      </div>

      {rightButtons?.map((item, idx) => (
        <span
          key={idx}
          onClick={(e) => {
            e.stopPropagation();
            item.onClick?.(e);
          }}
        >
          {item.component}
        </span>
      ))}
    </nav>
  );
};

export default Navbar;
